export const PANEL_SCROLL_TOP_GAP_PX = 8
export const PANEL_SCROLL_TOP_INSET_FALLBACK_PX = 48
export const PANEL_SCROLL_BOTTOM_GAP_PX = 8
export const PANEL_SCROLL_END_ANCHOR_SELECTOR = '[data-panel-scroll-end-anchor="true"]'
// A thread's post rows, which the end gutter falls back to when nothing marks a
// semantic end anchor (the fixed bottom editor marks none). Every post's root
// carries it, and so must anything that renders in a post's place, like a
// direct message's head: a thread with no row has no gutter, and its card stops
// short of the composer.
export const PANEL_SCROLL_ROW_SELECTOR = '[data-testid="answer-root"]'
export const PANEL_SCROLL_TOP_OBSTRUCTION_SELECTOR = '[data-panel-scroll-top-obstruction="true"]'
export const PANEL_SCROLL_BOTTOM_OBSTRUCTION_SELECTOR = '[data-new-quest-button]'

type PanelEndGutterMetrics = {
    clientHeight: number
    topInset: number
    contentBottom: number
    endAnchorTop: number
    bottomInset?: number
}

export function calculatePanelEndGutterHeight({
    clientHeight,
    topInset,
    contentBottom,
    endAnchorTop,
    bottomInset = 0,
}: PanelEndGutterMetrics) {
    if (
        !Number.isFinite(clientHeight) ||
        !Number.isFinite(topInset) ||
        !Number.isFinite(contentBottom) ||
        !Number.isFinite(endAnchorTop) ||
        !Number.isFinite(bottomInset) ||
        clientHeight <= 0 ||
        topInset < 0 ||
        bottomInset < 0 ||
        contentBottom < endAnchorTop
    ) {
        return 0
    }

    const trailingContentHeight = contentBottom - endAnchorTop
    return Math.max(
        Math.ceil(bottomInset),
        Math.ceil(clientHeight - topInset - trailingContentHeight),
    )
}

export function getPanelScrollTopInset(scrollContainer: HTMLElement) {
    const panelContainer = scrollContainer.parentElement
    const obstruction = panelContainer?.querySelector<HTMLElement>(
        PANEL_SCROLL_TOP_OBSTRUCTION_SELECTOR,
    )
    if (!obstruction) return PANEL_SCROLL_TOP_INSET_FALLBACK_PX

    const scrollRect = scrollContainer.getBoundingClientRect()
    const obstructionRect = obstruction.getBoundingClientRect()
    const measuredInset = Math.ceil(
        obstructionRect.bottom - scrollRect.top + PANEL_SCROLL_TOP_GAP_PX,
    )

    if (
        !Number.isFinite(measuredInset) ||
        measuredInset <= 0 ||
        measuredInset >= scrollContainer.clientHeight
    ) {
        return PANEL_SCROLL_TOP_INSET_FALLBACK_PX
    }

    return Math.max(PANEL_SCROLL_TOP_INSET_FALLBACK_PX, measuredInset)
}

export function getPanelScrollBottomInset(scrollContainer: HTMLElement) {
    const obstruction = scrollContainer.ownerDocument.querySelector<HTMLElement>(
        PANEL_SCROLL_BOTTOM_OBSTRUCTION_SELECTOR,
    )
    if (!obstruction || obstruction.getClientRects().length === 0) return 0

    const scrollRect = scrollContainer.getBoundingClientRect()
    const obstructionRect = obstruction.getBoundingClientRect()
    // The button is centred on the window rather than on a panel, so in a
    // multi-panel layout it covers only the panel it actually sits over. A
    // purely vertical inset would shrink every panel by a button none of the
    // others can see. (Single-panel and mobile always overlap, so this is a
    // no-op there.)
    if (obstructionRect.right <= scrollRect.left || obstructionRect.left >= scrollRect.right) {
        return 0
    }

    const measuredInset = Math.ceil(
        scrollRect.bottom - obstructionRect.top + PANEL_SCROLL_BOTTOM_GAP_PX,
    )

    if (
        !Number.isFinite(measuredInset) ||
        measuredInset <= 0 ||
        measuredInset >= scrollContainer.clientHeight
    ) {
        return 0
    }

    return measuredInset
}
